import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, InputLabel, TextField, Typography, IconButton } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import * as Yup from 'yup';
import { useChangePasswordByTokenMutation } from "../../redux/changePassword/changePassword.api";
import MUIButton from "../../reusable_components/components/MUIButton";
import MUICancelButton from "../../reusable_components/components/MuiCancelButton";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { OnLogout } from "../../redux/common/common.slice";
import { useContext, useState } from "react";
import { SnackbarContext } from "../../reusable_components/components/Snackbar";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const ChangePassword = () => {
    const dispatch = useDispatch();
    const { Snackbar } = useContext(SnackbarContext);

    // APIs
    const [changePasswordByToken] = useChangePasswordByTokenMutation();

    // Password visibility states
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const togglePasswordVisibility = (field) => {
        if (field === "currentPassword") {
            setShowCurrentPassword(!showCurrentPassword);
        } else if (field === "newPassword") {
            setShowNewPassword(!showNewPassword);
        } else if (field === "confirmPassword") {
            setShowConfirmPassword(!showConfirmPassword);
        }
    };

    const validationSchema = Yup.object().shape({
        newPassword: Yup.string()
            .required("New Password is required")
            .matches(/^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{5,}$/, "Must contain at least 1 Capital Letter, 1 Small Letter, 1 Special Character, 1 Number!"),
        confirmPassword: Yup.string()
            .required("Confirm Password is required")
            .oneOf([Yup.ref("newPassword")], "Passwords must match"),
        currentPassword: Yup.string().required("Current Password is required"),
    });

    const resolver = yupResolver(validationSchema);

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver,
        mode: "onChange",
        defaultValues: {
            newPassword: "",
            confirmPassword: "",
            currentPassword: "",
        },
    });

    const { Profile } = useSelector((state) => state.common);
    const id = Profile?.profile?.id;
    const userRole=Profile?.profile?.role

    const onSubmit = async (data) => {
        if (data.currentPassword === data.newPassword) {
            Snackbar("New password must be different from current password", "error");
            return;
        }
        try {
            const payload = {
                currentPassword: data?.currentPassword,
                confirmPassword: data?.confirmPassword,
                newPassword: data?.newPassword,
                userName: id,
            };
            const response = await changePasswordByToken(payload).unwrap();
            if (response?.status === 200) {
                Snackbar(response?.response?.message, "success");
                dispatch(OnLogout());
                localStorage.clear();
                window.location.replace("/");
            }
        } catch (error) {
            if (error?.status === 400) {
                Snackbar(error?.data?.data[0]?.error_message, "error");
            } else {
                Snackbar("Network error", "error");
            }
        }
    };

    return (
        <Box sx={{ alignItems: "center", display: "flex", justifyContent: "center", marginTop: "150px" }}>
            <Box sx={{ backgroundColor: "#fff", height: "100%", minWidth: "20px", alignItems: "center", display: "flex" }}>
                <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
                    <Grid sx={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "36px" }}>
                        <Grid>
                            <Typography className="title">Change Password</Typography>

                            {/* Current Password */}
                            <Grid sx={{ display: "flex", flexDirection: "row", marginTop: "20px" }}>
                                <InputLabel className="inputlabels" sx={{ width: "200px", marginTop: "10px" }}>
                                    Current Password <span style={{ color: "red" }}>*</span>
                                </InputLabel>
                                <Controller
                                    control={control}
                                    name={"currentPassword"}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            type={showCurrentPassword ? "text" : "password"}
                                            placeholder="Enter Current Password"
                                            variant="outlined"
                                            size="small"
                                            sx={{ width: "400px" }}
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton onClick={() => togglePasswordVisibility("currentPassword")}>
                                                        {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                ),
                                            }}
                                            helperText={<span style={{ color: "red" }}>{errors?.currentPassword?.message || ""}</span>}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* New Password */}
                            <Grid sx={{ display: "flex", flexDirection: "row", marginTop: "20px" }}>
                                <InputLabel className="inputlabels" sx={{ width: "200px", marginTop: "10px" }}>
                                    New Password <span style={{ color: "red" }}>*</span>
                                </InputLabel>
                                <Controller
                                    control={control}
                                    name={"newPassword"}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            type={showNewPassword ? "text" : "password"}
                                            placeholder="Enter New Password"
                                            variant="outlined"
                                            size="small"
                                            sx={{ width: "400px" }}
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton onClick={() => togglePasswordVisibility("newPassword")}>
                                                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                ),
                                            }}
                                            helperText={<span style={{ color: "red" }}>{errors?.newPassword?.message || ""}</span>}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Confirm Password */}
                            <Grid sx={{ display: "flex", flexDirection: "row", marginTop: "10px" }}>
                                <InputLabel className="inputlabels" sx={{ width: "200px", marginTop: "10px" }}>
                                    Confirm Password <span style={{ color: "red" }}>*</span>
                                </InputLabel>
                                <Controller
                                    control={control}
                                    name={"confirmPassword"}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            type={showConfirmPassword ? "text" : "password"}
                                            placeholder="Enter Confirm Password"
                                            variant="outlined"
                                            size="small"
                                            sx={{ width: "400px" }}
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton onClick={() => togglePasswordVisibility("confirmPassword")}>
                                                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                ),
                                            }}
                                            helperText={<span style={{ color: "red" }}>{errors?.confirmPassword?.message || ""}</span>}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid sx={{ display: "flex", justifyContent: "center", marginBottom: "20px", gap: "35px" }}>
                        <MUIButton title="Confirm" type="Submit" />
                        <MUICancelButton title="Cancel" component={Link} to={userRole==="admin" ? "/dashboard":"/jobpost"} />
                    </Grid>
                </form>
            </Box>
        </Box>
    );
};

export default ChangePassword;
